"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Instagram, Facebook } from "lucide-react";
import { EVENT, KV } from "./data";
import { display } from "@/lib/utils";

const pad = (n: number) => String(Math.max(0, n)).padStart(2, "0");

const split = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
};

/** Race-day lock screen: the whole site collapses to this single view until launch. */
const ComingSoon: React.FC = () => {
  const [now, setNow] = useState<number | null>(null);
  const target = new Date(EVENT.startsAt).getTime();

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = now === null ? null : target - now;
  const t = remaining === null ? null : split(remaining);

  const cells = [
    { value: t ? String(t.days).padStart(3, "0") : "---", label: "Días" },
    { value: t ? pad(t.hours) : "--", label: "Horas" },
    { value: t ? pad(t.minutes) : "--", label: "Minutos" },
    { value: t ? pad(t.seconds) : "--", label: "Segundos" },
  ];

  return (
    <div className="relative flex min-h-[100svh] w-full flex-col overflow-hidden bg-brand-blue text-white">
      {/* Diagonal guide lines */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <g stroke="rgba(255,255,255,0.16)" strokeWidth="1" vectorEffect="non-scaling-stroke">
          <line x1="0" y1="62" x2="100" y2="14" vectorEffect="non-scaling-stroke" />
          <line x1="0" y1="98" x2="100" y2="50" vectorEffect="non-scaling-stroke" />
          <line x1="22" y1="0" x2="58" y2="100" vectorEffect="non-scaling-stroke" />
          <line x1="52" y1="0" x2="88" y2="100" vectorEffect="non-scaling-stroke" />
          <line x1="0" y1="26" x2="100" y2="-22" vectorEffect="non-scaling-stroke" />
        </g>
      </svg>

      {/* KV sun + wave line-art */}
      <div className="pointer-events-none absolute -right-[10%] -top-[6%] w-[46vw] opacity-70 md:-top-[4%] md:w-[30vw]">
        <Image
          src={KV.sunWaves}
          alt=""
          width={2400}
          height={1080}
          priority
          sizes="(max-width: 768px) 46vw, 30vw"
          className="h-auto w-full"
        />
      </div>

      {/* Main content */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center gap-7 px-4 py-16 text-center md:gap-8 md:px-8">
        <h1 className="mes-display text-[clamp(1.75rem,7.5vw,9rem)] leading-[0.88]">{display("Próximamente")}</h1>

        <p className="max-w-xl text-sm leading-relaxed opacity-90 md:text-lg">
          Estamos preparando la línea de salida. Muy pronto abrimos las inscripciones para 10KM, 21KM y 42KM.
        </p>

        <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-8 sm:flex sm:items-start sm:justify-center sm:gap-5 md:gap-8">
          {cells.map((c, i) => (
            <React.Fragment key={c.label}>
              <div className="flex flex-col items-center">
                <span
                  className="mes-display text-[19vw] leading-none tabular-nums sm:text-[8.5vw]"
                  suppressHydrationWarning
                >
                  {c.value}
                </span>
                <span className="mes-label mt-3 opacity-80">{c.label}</span>
              </div>
              {i < cells.length - 1 && (
                <span className="hidden pt-[1vw] text-[6vw] leading-none opacity-50 sm:inline mes-display">
                  :
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </main>

      {/* Sunburst mark + horizon line */}
      <div className="pointer-events-none absolute left-4 z-20 w-24 md:left-8 md:w-36 [@media(max-height:500px)]:hidden" style={{ bottom: "5.5rem" }}>
        <Image src={KV.sunburst} alt="" width={1600} height={813} sizes="144px" className="h-auto w-full" />
        <div className="-mt-[1px] h-[2px] bg-white" />
      </div>

      {/* Footer */}
      <footer className="mes-label relative z-10 flex flex-wrap items-center justify-between gap-4 border-t border-white/15 px-6 py-5 opacity-85 md:px-10">
        <span>© 2027 {EVENT.organizer}</span>
        <div className="flex items-center gap-2.5">
          <a
            href={EVENT.instagram}
            target="_blank"
            rel="noreferrer"
            aria-label="Instagram"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/35"
          >
            <Instagram className="h-4 w-4" strokeWidth={1.8} />
          </a>
          <a
            href={EVENT.facebook}
            target="_blank"
            rel="noreferrer"
            aria-label="Facebook"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/35"
          >
            <Facebook className="h-4 w-4" strokeWidth={1.8} />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default ComingSoon;
